/**
 * Custom pet portrait builder — Step: format + size.
 *
 * Keeps the chosen format/size (and the matching variant) in
 * window.__nbWallState, shows each choice's price, keeps the product
 * page's main price in step, and announces changes with a
 * 'nb-wall:selection-changed' event on document (the design step
 * re-renders at the new shape).
 *
 * window.NavaBirdWallFormat.select(formatValue, sizeValue) lets the
 * design step's Size panel change the choice without going back.
 */
(function () {
  const DEFAULT_FORMAT_KEY = 'wood';
  const DEFAULT_SIZE = /18\s*[x×]\s*24/i;

  // [width, height] in inches. Portrait (taller than wide) unless the step
  // has data-orientation="landscape" (house portrait: wide prints).
  let LANDSCAPE = false;
  function parseSize(value) {
    const m = String(value).match(/(\d+(?:\.\d+)?)\s*[x×X]\s*(\d+(?:\.\d+)?)/);
    if (!m) return LANDSCAPE ? [16, 12] : [12, 16];
    const a = parseFloat(m[1]);
    const b = parseFloat(m[2]);
    return LANDSCAPE ? [Math.max(a, b), Math.min(a, b)] : [Math.min(a, b), Math.max(a, b)];
  }

  // Every placeholder Shopify uses in money formats: with Markets the page
  // can be in another currency (e.g. SEK: "{{amount_with_space_separator}} kr").
  // [decimals, thousands separator, decimal separator]
  const MONEY_PLACEHOLDERS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
    amount_with_apostrophe_separator: [2, "'", '.'],
  };

  function formatAmount(cents, [decimals, thousands, decimal]) {
    const fixed = (cents / 100).toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }

  function formatMoney(cents, format) {
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) =>
      MONEY_PLACEHOLDERS[name] ? formatAmount(cents, MONEY_PLACEHOLDERS[name]) : match
    );
  }

  // "$45.00" -> "$45", "782,00 kr" -> "782 kr" when the price is whole
  // (choice labels stay short).
  function shortMoney(cents, format) {
    const s = formatMoney(cents, format);
    return cents % 100 === 0 ? s.replace(/[.,]00(?!\d)/, '') : s;
  }

  function init() {
    const step = document.querySelector('[data-step="format"]');
    const dataEl = document.querySelector('[data-nb-wall-product]');
    if (!step || !dataEl) return;
    const data = JSON.parse(dataEl.textContent);
    LANDSCAPE = step.dataset.orientation === 'landscape';

    const formatBtns = Array.from(step.querySelectorAll('[data-format-value]'));
    const sizesEl = step.querySelector('[data-sizes]');
    const sizeBlock = step.querySelector('[data-size-block]');
    const noteLine = step.querySelector('[data-format-note-line]');
    const continueBtn = step.querySelector('[data-continue]');

    // Smallest to largest (Shopify's own order can shift as variants change),
    // each with a little rectangle drawn to scale.
    const sizeBtns = Array.from(step.querySelectorAll('[data-size-value]')).sort((a, b) => {
      const [aw, ah] = parseSize(a.dataset.sizeValue);
      const [bw, bh] = parseSize(b.dataset.sizeValue);
      return aw * ah - bw * bh;
    });
    sizeBtns.forEach((b) => sizesEl.appendChild(b));
    // Each rectangle keeps its print's real shape. Its size grows with the
    // print but on a compressed scale (the smallest is ~45% of the largest
    // instead of ~30%), so 8x10 still reads as a frame, not a dot.
    // (Landscape: the long side is the width, so the scale follows it.)
    const longSide = (wh) => (LANDSCAPE ? wh[0] : wh[1]);
    const heights = sizeBtns.map((b) => longSide(parseSize(b.dataset.sizeValue)));
    const minH = Math.min(...heights);
    const maxH = Math.max(...heights);
    const SMALLEST_SHARE = 0.45;
    sizeBtns.forEach((b) => {
      const [w, h] = parseSize(b.dataset.sizeValue);
      const long = longSide([w, h]);
      const share = maxH === minH ? 1 : SMALLEST_SHARE + (1 - SMALLEST_SHARE) * ((long - minH) / (maxH - minH));
      const rect = b.querySelector('[data-size-rect]');
      if (rect && LANDSCAPE) {
        rect.style.width = `${share * 100}%`;
        rect.style.height = `${share * (h / w) * 100}%`;
      } else if (rect) {
        rect.style.height = `${share * 100}%`;
        rect.style.width = `${share * (w / h) * 100}%`;
      }
    });

    const formatOption = data.options.find((o) => o.name === 'Format');
    const sizeOption = data.options.find((o) => o.name === 'Size');

    function findVariant(formatValue, sizeValue) {
      return data.variants.find(
        (v) =>
          (!formatOption || v.options[formatOption.position - 1] === formatValue) &&
          (!sizeOption || v.options[sizeOption.position - 1] === sizeValue)
      );
    }

    // Sizes that exist with at least one non-digital format.
    const printSizeValues = new Set(
      sizeBtns
        .map((b) => b.dataset.sizeValue)
        .filter((value) => formatBtns.some((fb) => fb.dataset.formatKey !== 'digital' && findVariant(fb.dataset.formatValue, value)))
    );
    if (!formatOption || !printSizeValues.size) sizeBtns.forEach((b) => printSizeValues.add(b.dataset.sizeValue));

    const state = {
      formatValue: null,
      formatKey: null,
      sizeValue: null,
      sizeInches: LANDSCAPE ? [16, 12] : [12, 16],
      ratio: LANDSCAPE ? 12 / 16 : 16 / 12,
      variant: null,
    };
    window.__nbWallState = state;

    // A digital file has no size choice: one variant, whatever its size value.
    const isDigital = (formatValue) => {
      const btn = formatBtns.find((b) => b.dataset.formatValue === formatValue);
      return !!btn && btn.dataset.formatKey === 'digital';
    };
    function onlySizeFor(formatValue) {
      const b = sizeBtns.find((btn) => findVariant(formatValue, btn.dataset.sizeValue));
      return b ? b.dataset.sizeValue : null;
    }
    let lastFrameSize = null; // restored when switching back from digital

    function cheapestFor(formatValue) {
      const prices = sizeBtns
        .map((b) => findVariant(formatValue, b.dataset.sizeValue))
        .filter((v) => v && v.available)
        .map((v) => v.price);
      return prices.length ? Math.min(...prices) : null;
    }

    // Main price block on the product page follows the chosen variant.
    const builderEl = document.querySelector('[data-instant-artwork-builder]');
    const priceBlock = builderEl && document.getElementById(`price-${builderEl.dataset.gallerySectionId}`);
    function syncMainPrice() {
      if (!state.variant || !priceBlock) return;
      const text = formatMoney(state.variant.price, data.moneyWithCurrencyFormat);
      priceBlock.querySelectorAll('.price-item--regular, .price-item--sale').forEach((el) => {
        el.textContent = text;
      });
    }

    // Like the other NavaBird builders: no prices on the frame tiles (the
    // "Subtotal:" line shows the price), and on the size tiles only a "+$20"
    // badge for how much more a size costs than the smallest one.
    function render() {
      formatBtns.forEach((btn) => {
        const on = btn.dataset.formatValue === state.formatValue;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      const cheapest = cheapestFor(state.formatValue);
      sizeBtns.forEach((btn) => {
        const on = btn.dataset.sizeValue === state.sizeValue;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        const v = findVariant(state.formatValue, btn.dataset.sizeValue);
        const priceEl = btn.querySelector('[data-size-price]');
        const diff = v && cheapest !== null ? v.price - cheapest : 0;
        priceEl.textContent = diff > 0 ? `+${shortMoney(diff, data.moneyFormat)}` : '';
        btn.disabled = data.variants.length > 0 && !!formatOption && (!v || !v.available);
        // A size only a digital file uses (e.g. "Digital File") isn't a print size.
        btn.hidden = !!formatOption && !printSizeValues.has(btn.dataset.sizeValue);
      });
      const digital = isDigital(state.formatValue);
      if (sizeBlock) sizeBlock.hidden = digital;
      const selectedBtn = formatBtns.find((b) => b.dataset.formatValue === state.formatValue);
      if (noteLine) noteLine.textContent = selectedBtn ? `✓ ${selectedBtn.dataset.formatNote}` : '';
      continueBtn.disabled = !(state.formatValue && state.sizeValue);
      syncMainPrice();
      // "Subtotal:" line above each step's button (same as the other builders)
      const subtotal = state.variant ? formatMoney(state.variant.price, data.moneyFormat) : '';
      document.querySelectorAll('[data-nb-subtotal]').forEach((el) => {
        el.textContent = subtotal;
      });
    }

    function select(formatValue, sizeValue) {
      if (formatValue) {
        const btn = formatBtns.find((b) => b.dataset.formatValue === formatValue);
        if (btn) {
          const wasDigital = isDigital(state.formatValue);
          state.formatValue = formatValue;
          state.formatKey = btn.dataset.formatKey;
          if (isDigital(formatValue)) {
            if (!wasDigital && state.sizeValue) lastFrameSize = state.sizeValue;
            sizeValue = sizeValue || onlySizeFor(formatValue);
          } else if (wasDigital && !sizeValue) {
            sizeValue = lastFrameSize || state.sizeValue;
          }
        }
      }
      if (sizeValue) {
        state.sizeValue = sizeValue;
        state.sizeInches = parseSize(sizeValue);
        state.ratio = state.sizeInches[1] / state.sizeInches[0];
      }
      state.variant = state.formatValue && state.sizeValue ? findVariant(state.formatValue, state.sizeValue) || null : null;
      render();
      if (formatValue) showProductPhotoFor(state.formatKey);
      document.dispatchEvent(new CustomEvent('nb-wall:selection-changed', { detail: { ...state } }));
    }

    // The big gallery jumps to a product photo of the chosen frame (matched
    // by the photo's alt text, e.g. "...in a black frame"). Skipped once the
    // customer's own portrait scenes are in the gallery — those already
    // follow the frame.
    const PHOTO_WORDS = { black: 'black frame', white: 'white frame', wood: 'wood frame', canvas: 'canvas', digital: 'digital file' };
    let firstSelect = true;
    function showProductPhotoFor(key) {
      if (firstSelect) {
        firstSelect = false; // page load: keep the gallery as it opens
        return;
      }
      if (document.querySelector('[data-nb-scene]')) return;
      const words = PHOTO_WORDS[key];
      const builder = document.querySelector('[data-instant-artwork-builder]');
      const sectionId = builder && builder.dataset.gallerySectionId;
      const gallery = sectionId && document.getElementById(`MediaGallery-${sectionId}`);
      if (!words || !gallery || !gallery.setActiveMedia) return;
      const slide = Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]')).find((li) => {
        const img = li.querySelector('img');
        return img && (img.alt || '').toLowerCase().includes(words);
      });
      if (slide) gallery.setActiveMedia(slide.dataset.mediaId, false);
    }

    formatBtns.forEach((btn) => btn.addEventListener('click', () => select(btn.dataset.formatValue, null)));
    sizeBtns.forEach((btn) => btn.addEventListener('click', () => select(null, btn.dataset.sizeValue)));

    // Sensible starting point: a framed 12x16 if offered.
    const startFormat = formatBtns.find((b) => b.dataset.formatKey === DEFAULT_FORMAT_KEY) || formatBtns[0];
    const wantedSize = step.dataset.defaultSize;
    const startSize =
      sizeBtns.find((b) => (wantedSize ? b.dataset.sizeValue === wantedSize : DEFAULT_SIZE.test(b.dataset.sizeValue))) ||
      sizeBtns[Math.floor(sizeBtns.length / 2)];
    select(startFormat && startFormat.dataset.formatValue, startSize && startSize.dataset.sizeValue);

    window.NavaBirdWallFormat = {
      select,
      formats: () => formatBtns.map((b) => ({ value: b.dataset.formatValue, key: b.dataset.formatKey })),
      sizes: () => (isDigital(state.formatValue) ? [] : sizeBtns.map((b) => b.dataset.sizeValue).filter((v) => printSizeValues.has(v))),
      priceLabel: (formatValue, sizeValue) => {
        const v = findVariant(formatValue, sizeValue);
        return v ? shortMoney(v.price, data.moneyFormat) : '';
      },
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
